<script setup lang="ts">
import type {
  CrashAnalysisWindowKey,
  CrashGameName,
} from '~/types/crash-pre-game';
import { CRASH_ANALYSIS_WINDOWS } from '~/types/crash-pre-game';

type Props = {
  /** `game_name` da whitelist (aviator | aviator-2). */
  gameName: CrashGameName;
};

const { gameName } = defineProps<Props>();

const {
  data,
  isFirstLoad,
  isEmpty,
  isFatalError,
  isAbandoned,
  secondsSinceUpdate,
  errorKind,
  retry,
} = useCrashPreGame(() => gameName);

const candles = computed(() => data.value?.cycleHistory ?? []);

// A vela mais recente sobe pro topo da tela em destaque — e continua sendo a
// primeira célula da grade. Tirá-la da grade abria um buraco na contagem: a
// linha começava na segunda rodada e o total nunca fechava com o do core.
const lastCandle = computed(() => candles.value[0] ?? null);

const analysis = computed(() => data.value?.analysis ?? null);

// Só entram as janelas que o core mandou, na ordem da constante (da mais curta
// pra mais longa). Jogo com coletor novo tem "5m" e mais nada.
const windowKeys = computed<CrashAnalysisWindowKey[]>(() => {
  const windows = analysis.value?.windows;

  if (!windows) return [];

  return CRASH_ANALYSIS_WINDOWS.filter((key) => Boolean(windows[key]));
});

// 30m é a janela de abertura — amostra grande o bastante pra não oscilar a cada
// vela e curta o bastante pra ainda ser "agora". Se ela não veio, abre na mais
// longa disponível.
const selectedWindow = ref<CrashAnalysisWindowKey | null>(null);

const activeKey = computed<CrashAnalysisWindowKey | null>(() => {
  const keys = windowKeys.value;

  if (selectedWindow.value && keys.includes(selectedWindow.value)) {
    return selectedWindow.value;
  }

  if (keys.includes('30m')) return '30m';

  return keys[keys.length - 1] ?? null;
});

const activeWindow = computed(() => {
  if (!activeKey.value) return null;

  return analysis.value?.windows[activeKey.value] ?? null;
});

// A janela escolhida vale pros DOIS blocos (proporção e histograma): são duas
// leituras da mesma amostra, e deixá-las em janelas diferentes faria os números
// de uma não fecharem com os da outra.
const windowModel = computed<CrashAnalysisWindowKey>({
  get: () => activeKey.value ?? '30m',
  set: (value) => {
    selectedWindow.value = value;
  },
});

const ranges = computed(() => analysis.value?.ranges ?? []);

// Com o coletor parado e a chave da análise LIGADA pro jogo, o core ainda monta
// o bloco — só que zerado (janelas com total 0, faixas com perRounds null). Um
// "Foram 0 rodadas" com barra vazia não informa nada; os blocos só entram
// quando alguma janela tem rodada.
const hasWindowData = computed(() =>
  windowKeys.value.some(
    (key) => (analysis.value?.windows[key]?.total ?? 0) > 0,
  ),
);

// Faixa sem `perRounds` é faixa que não saiu na amostra — o card apagado diz
// isso. Mas se TODAS estiverem assim, não há amostra nenhuma e o bloco some.
const hasRangeData = computed(() =>
  ranges.value.some((item) => item.perRounds !== null),
);

const liveLabel = computed(() => {
  const seconds = secondsSinceUpdate.value;

  if (seconds === null) return null;

  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) return `${minutes}min`;

  return `${Math.floor(minutes / 60)}h`;
});

const errorMessage = computed(() => {
  if (!isFatalError.value) return null;

  // 422 = `game_name` fora da whitelist do core. Não é erro pra mostrar: é o
  // core dizendo que este jogo não tem conteúdo — e aí a tela não existe (ver
  // `hasContent`).
  if (errorKind.value === 'invalid_game') return null;

  return 'Não foi possível carregar o histórico de rodadas.';
});

// A TELA INTEIRA SÓ EXISTE COM VELA.
//
// Os dois jogos estão na whitelist do core (config/crash.php), então pedir o
// Aviator 1 com o coletor desligado — que é a situação de produção hoje — volta
// 200 com `cycleHistory: []`, não 422. Sem este gate a página do Aviator
// ganharia um painel "aguardando as primeiras velas" parado pra sempre.
//
// Nada de aviso, nada de placeholder: jogo sem conteúdo no core fica
// EXATAMENTE como um jogo comum. Quando o coletor daquela instância subir, o
// painel aparece sozinho no primeiro tick, sem deploy.
const hasContent = computed(() => {
  if (isEmpty.value) return false;

  // 422: o core tirou o jogo da whitelist. Mesmo desfecho.
  if (isFatalError.value && errorKind.value === 'invalid_game') return false;

  // Coletor abandonado (uma hora sem vela): a tela sai do ar inteira. O quanto o
  // dado envelheceu quem conta é o "ao vivo há…" do card de cima — daí pra
  // frente não há mais o que contar, só o que esconder.
  if (isAbandoned.value) return false;

  return true;
});

const canRetry = computed(
  () => isFatalError.value && errorKind.value !== 'invalid_game',
);

// Resumos que continuam visíveis com o bloco fechado.
const candlesSummary = computed(() =>
  candles.value.length ? `${candles.value.length} registradas` : '',
);

const colorsSummary = computed(() => {
  const total = activeWindow.value?.total;

  if (!total) return '';

  return `${total} ${total === 1 ? 'rodada' : 'rodadas'}`;
});

const BUCKET_LABELS: Record<string, string> = {
  '<2x': 'menos de 2X',
  '2-5x': 'de 2X a 5X',
  '5-10x': 'de 5X a 10X',
  '10-50x': 'de 10X a 50X',
  '50x+': 'mais de 50X',
};

const histSummary = computed(() => {
  const slices = activeWindow.value?.distribution ?? [];

  if (!slices.length) return '';

  const top = slices.reduce((acc, slice) =>
    slice.count > acc.count ? slice : acc,
  );

  if (!top.count) return '';

  return `A maioria ficou ${BUCKET_LABELS[top.bucket] ?? top.bucket}`;
});

const freqSummary = computed(() =>
  ranges.value.length ? `${ranges.value.length} faixas` : '',
);
</script>

<template>
  <div
    v-if="hasContent"
    class="crash-pregame"
  >
    <!-- Erro cheio: só quando NUNCA houve um 200. Com payload em tela a falha
         vira o aviso discreto acima e as velas continuam onde estavam. -->
    <div
      v-if="errorMessage"
      class="crash-pregame__state"
    >
      <p class="crash-pregame__state-text">{{ errorMessage }}</p>

      <button
        v-if="canRetry"
        class="crash-pregame__retry"
        type="button"
        @click="retry"
      >
        Tentar novamente
      </button>
    </div>

    <div
      v-else-if="isFirstLoad"
      class="crash-pregame__state crash-pregame__state--skeleton skeleton-loading"
    />

    <template v-else>
      <!-- O "ao vivo" mora aqui dentro: ele fala da última rodada, e o cabeçalho
           que o segurava repetia o nome do jogo que já está na página. Ele é
           também o único termômetro de idade do dado agora — mostra "há 12min"
           sem alarde, e depois de uma hora a tela some por conta própria. -->
      <CasinoGameCrashLastRound
        v-if="lastCandle"
        :candle="lastCandle"
        :live="liveLabel"
      />

      <CasinoGameCrashBlock
        accent="cyan"
        help="A cor diz até onde o avião foi antes de cair. A primeira é a rodada mais recente."
        :summary="candlesSummary"
        title="As rodadas anteriores"
      >
        <template #icon>
          <svg
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
          >
            <path
              clip-rule="evenodd"
              d="M13.5 6H8.75V8.5H13.5V6ZM7.25 6H2.5V8.5H7.25V6ZM1 6V5C1 3.34315 2.34315 2 4 2H12C13.6569 2 15 3.34315 15 5L15 6L15 11C15 12.6569 13.6569 14 12 14H4C2.34315 14 1 12.6569 1 11V6ZM8.75 10H13.5V11C13.5 11.8284 12.8284 12.5 12 12.5H8.75V10ZM2.5 10H7.25V12.5H4C3.17157 12.5 2.5 11.8284 2.5 11V10Z"
              fill="currentColor"
              fill-rule="evenodd"
            />
          </svg>
        </template>

        <CasinoGameCrashSparkline :candles="candles" />

        <CasinoGameCrashCandleHistory :candles="candles" />
      </CasinoGameCrashBlock>

      <!-- Os três blocos de análise só existem com `analysis` preenchido: null
           é o gate de compliance do core fechado (CRASH_ANALYSIS_ENABLED), e
           ligá-lo não precisa de deploy do front. -->
      <template v-if="analysis">
        <CasinoGameCrashBlock
          v-if="activeWindow && hasWindowData"
          accent="purple"
          help="Escolha quanto tempo para trás você quer contar. A barra mostra quanto cada cor ocupou nesse período."
          :summary="colorsSummary"
          title="Quantas de cada cor saíram"
        >
          <template #icon>
            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
            >
              <path
                clip-rule="evenodd"
                d="M14 12C14 12.2761 13.7761 12.5 13.5 12.5H2.5C2.22386 12.5 2 12.2761 2 12L2 11C2 10.9999 2 11.0001 2 11C2.00019 10.724 2.22397 10.5 2.5 10.5H9.5H12H13.5C13.7761 10.5 14 10.7239 14 11V12ZM14 8.5C14 8.77614 13.7761 9 13.5 9H12C11.7242 9 11.5005 8.77662 11.5 8.50088V7.5C11.5 7.22409 11.7235 7.00038 11.9993 7H13.5C13.7761 7 14 7.22386 14 7.5V8.5ZM14 5V4C14 3.72386 13.7761 3.5 13.5 3.5H8C7.72386 3.5 7.5 3.72386 7.5 4V5C7.5 5.27604 7.72368 5.49983 7.99968 5.5H9.5L11.9987 5.5L12 5.5L13.5 5.5C13.7761 5.5 14 5.27614 14 5ZM2.5 3.5C2.24112 3.5 2.02819 3.69675 2.00258 3.94888C2.00087 3.96569 2 3.98274 2 4L2 5C2 4.99988 2 5.00012 2 5C2.00019 5.27598 2.22397 5.5 2.5 5.5H5.5C5.77606 5.4999 6 5.27608 6 5V4C6 3.72386 5.77614 3.5 5.5 3.5L2.5 3.5ZM2 7.49965C2 7.49953 2 7.49977 2 7.49965L2 8.5C2 8.49988 2 8.50012 2 8.5C2.00019 8.77598 2.22397 9 2.5 9L9.5 9C9.77595 8.99977 10 8.776 10 8.5V7.5C9.99952 7.22439 9.77604 7.0002 9.50036 7H8L5.50106 7L5.5 7L2.5 7C2.22397 7 2.00019 7.22367 2 7.49965ZM0.5 12C0.5 13.1046 1.39543 14 2.5 14H13.5C14.6046 14 15.5 13.1046 15.5 12V11V8.5V7.5V5V4C15.5 2.89543 14.6046 2 13.5 2H8H5.5H2.5C1.46447 2 0.612746 2.787 0.510326 3.79551C0.503498 3.86275 0.5 3.93096 0.5 4V5V7.5V8.5V11V12Z"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>
          </template>

          <CasinoGameCrashColorWindow
            v-model="windowModel"
            :streak="analysis.streaks.current"
            :window="activeWindow"
            :window-keys="windowKeys"
          />
        </CasinoGameCrashBlock>

        <CasinoGameCrashBlock
          v-if="activeWindow && hasWindowData"
          accent="pink"
          help="Quanto mais comprida a barra, mais vezes a rodada parou naquela faixa."
          :summary="histSummary"
          title="Até onde as rodadas chegaram"
        >
          <template #icon>
            <svg
              fill="none"
              height="16"
              viewBox="-1 -1 18 18"
              width="16"
            >
              <path
                d="M0 12C0 10.8954 0.895431 10 2 10C3.10457 10 4 10.8954 4 12V14C4 15.1046 3.10457 16 2 16C0.895431 16 0 15.1046 0 14V12Z"
                fill="currentColor"
              />
              <path
                d="M6 7C6 5.89543 6.89543 5 8 5C9.10457 5 10 5.89543 10 7V14C10 15.1046 9.10457 16 8 16C6.89543 16 6 15.1046 6 14V7Z"
                fill="currentColor"
              />
              <path
                d="M12 2C12 0.895431 12.8954 0 14 0C15.1046 0 16 0.895431 16 2V14C16 15.1046 15.1046 16 14 16C12.8954 16 12 15.1046 12 14V2Z"
                fill="currentColor"
              />
            </svg>
          </template>

          <!-- Mesma `key` das abas do bloco de cima: trocar de janela remonta
               o histograma, e as barras crescem de novo em vez de saltar. -->
          <CasinoGameCrashHistogram
            :key="windowModel"
            :distribution="activeWindow.distribution"
          />
        </CasinoGameCrashBlock>

        <CasinoGameCrashBlock
          v-if="hasRangeData"
          accent="good"
          help="É uma média do que já passou, nunca uma previsão do que vem."
          :summary="freqSummary"
          title="Frequência de multiplicadores"
        >
          <template #icon>
            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
            >
              <path
                clip-rule="evenodd"
                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM8.75 4.5C8.75 4.08579 8.41421 3.75 8 3.75C7.58579 3.75 7.25 4.08579 7.25 4.5V8C7.25 8.23607 7.36115 8.45836 7.55 8.6L9.55 10.1C9.88137 10.3485 10.3515 10.2814 10.6 9.95C10.8485 9.61863 10.7814 9.14853 10.45 8.9L8.75 7.625V4.5Z"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>
          </template>

          <CasinoGameCrashFrequency :ranges="ranges" />
        </CasinoGameCrashBlock>
      </template>

      <!-- Régua de compliance da tela: nada aqui é previsão. O divisor mora no
           <footer> (largura total, alinhado com os cards) e não no parágrafo:
           preso ao <p>, a linha encolhia junto com o max-width de leitura e o
           texto ficava órfão no desktop. A borda de baixo fecha o bloco contra
           a seção seguinte — estatísticas no mobile, vistos recentes no desktop. -->
      <footer class="crash-pregame__closing">
        <p class="crash-pregame__closing-text">
          Cada rodada é sorteada sozinha, sem nenhuma relação com a anterior.
          Tudo nesta tela é <b>o que já caiu</b>, nunca o que vem.
        </p>
      </footer>
    </template>
  </div>
</template>

<style scoped>
.crash-pregame {
  color: #e9eef9;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.55;
  /* Painel de dados não se seleciona nem se arrasta: no mobile, segurar o dedo
     numa vela pra abrir o bloco acabava virando seleção de texto, e no desktop
     arrastar deixava o fantasma da imagem atravessando a tela. */
  user-select: none;
  -webkit-user-select: none;
}

/* Vale pros SVGs dos ícones e pra qualquer imagem que entre aqui depois. */
.crash-pregame :deep(img),
.crash-pregame :deep(svg) {
  -webkit-user-drag: none;
}

.crash-pregame :deep(img) {
  pointer-events: none;
}

.crash-pregame__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px 14px;
  border: 1px solid #1c2740;
  border-radius: 16px;
  background: #0f1727;
  text-align: center;
}

.crash-pregame__state--skeleton {
  height: 180px;
  border: 0;
}

.crash-pregame__state-text {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 600;
}

.crash-pregame__retry {
  padding: 9px 16px;
  border: 1px solid #1c2740;
  border-radius: 12px;
  background: none;
  color: #37c6f0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.crash-pregame__closing {
  margin: 28px 0 0;
  padding: 22px 0;
  border-top: 1px solid #1c2740;
  border-bottom: 1px solid #1c2740;
}

.crash-pregame__closing-text {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  line-height: 1.6;
}

.crash-pregame__closing-text b {
  color: #e9eef9;
  font-weight: 700;
}

@media (min-width: 1024px) {
  .crash-pregame__closing {
    margin-top: 34px;
    padding: 24px 0;
  }

  /* O limite de leitura é do texto, não da régua: o divisor continua indo de
     ponta a ponta junto com os cards acima. */
  .crash-pregame__closing-text {
    max-width: 56ch;
  }
}
</style>
